import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { domainsApi, DomainConfig, buildMinimalCustomModePayload } from '../api/domains';
import { startMailboxGoogleOAuth, startFirstMailboxSyncIfNeeded } from '../api/mailboxes';
import { subscriptionApi } from '../api/subscription';
import { useAppBootstrap } from '../context/AppBootstrapContext';
import { navigateTo } from '../utils/browser';
import { Logo } from '../components/Logo';
import ModeLimitBlock from '../modes/ModeLimitBlock';
import { parseModeLimitFromApiError, type ModeLimitDetail } from '../utils/modeLimitMessaging';
import { CheckCircle, Eye, Filter, Plus, Power } from 'lucide-react';
import { BRAND_NAME } from '../brand';

let ConfettiComponent: React.ComponentType<any> | null = null;
try {
    ConfettiComponent = require('react-confetti').default;
} catch {
    ConfettiComponent = null;
}

type StepKey = 'domain' | 'mailbox' | 'trial' | 'profile' | 'finish';
type Toast = { type: 'success' | 'error'; message: string };

const steps: { key: StepKey; title: string; subtitle: string }[] = [
    { key: 'domain', title: 'Choose your focus', subtitle: 'Select the domain that fits your needs' },
    { key: 'mailbox', title: 'Connect Gmail', subtitle: 'Connect Gmail so mail can land in Triage. You still sort with Start / Snooze / Dismiss.' },
    { key: 'trial', title: 'Unlock Pro Trial', subtitle: 'Default: Yes — frictionless, no card needed for 14 days' },
    { key: 'profile', title: 'Your profile', subtitle: 'Just the basics to personalize your experience' },
    { key: 'finish', title: 'All set', subtitle: 'Review and launch into your workspace' },
];

const MAILBOX_STEP_INDEX = steps.findIndex((s) => s.key === 'mailbox');

const OnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { bootstrap, loading, refresh, setActiveDomain, updateProfile, completeOnboarding } = useAppBootstrap();
    const [domainOptions, setDomainOptions] = useState<DomainConfig[]>([]);
    const [stepIndex, setStepIndex] = useState(0);
    const [displayName, setDisplayName] = useState('');
    const [timezone, setTimezone] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [customModeOpen, setCustomModeOpen] = useState(false);
    const [customModeName, setCustomModeName] = useState('');
    const [customModeDescription, setCustomModeDescription] = useState('');
    const [creatingMode, setCreatingMode] = useState(false);
    const [modeLimitDetail, setModeLimitDetail] = useState<ModeLimitDetail | null>(null);
    const [toast, setToast] = useState<Toast | null>(null);
    const [trialOptIn, setTrialOptIn] = useState(true);
    const [trialSubmitting, setTrialSubmitting] = useState(false);
    const [showConfetti, setShowConfetti] = useState(false);

    const timezoneOptions = useMemo(() => {
        const supported = (Intl as any).supportedValuesOf?.('timeZone');
        if (Array.isArray(supported) && supported.length > 0) return supported as string[];
        return [
            'UTC',
            'America/Los_Angeles',
            'America/Denver',
            'America/Chicago',
            'America/New_York',
            'Europe/London',
            'Europe/Paris',
            'Asia/Tokyo',
            'Asia/Seoul',
            'Asia/Singapore',
            'Australia/Sydney',
        ];
    }, []);

    useEffect(() => {
        if (!toast) return;
        const timeout = window.setTimeout(() => setToast(null), 4500);
        return () => window.clearTimeout(timeout);
    }, [toast]);

    const pushToast = (type: Toast['type'], message: string) => {
        setToast({ type, message });
    };

    useEffect(() => {
        domainsApi.getDomains().then(setDomainOptions).catch(() => setDomainOptions([]));
    }, []);

    useEffect(() => {
        if (bootstrap?.user) {
            setDisplayName(bootstrap.user.display_name || '');
            setTimezone(bootstrap.user.timezone || '');
            setZipCode(bootstrap.user.zip_code || '');
        }
    }, [bootstrap]);

    useEffect(() => {
        const mailboxResult = searchParams.get('mailbox');
        if (!mailboxResult) return;
        const rawMessage = searchParams.get('message');

        const next = new URLSearchParams(searchParams);
        next.delete('mailbox');
        next.delete('message');
        setSearchParams(next, { replace: true });

        if (mailboxResult === 'success') {
            pushToast('success', 'Gmail connected successfully.');
            void (async () => {
                await refresh();
                try {
                    const started = await startFirstMailboxSyncIfNeeded();
                    if (started.length > 0) {
                        pushToast('success', 'First mailbox sync started.');
                    }
                } catch {
                    pushToast(
                        'success',
                        'Gmail connected. If mail does not appear shortly, open Gmail settings and use Start Sync.',
                    );
                }
            })();
        } else if (mailboxResult === 'error') {
            const msg = rawMessage?.replace(/\+/g, ' ') || 'Mailbox connection failed.';
            pushToast('error', msg);
        }
        if (MAILBOX_STEP_INDEX >= 0) setStepIndex(MAILBOX_STEP_INDEX);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const activeDomainKey = bootstrap?.active_domain_key || 'job_hunt';
    const currentStep = steps[stepIndex];

    const canContinue = useMemo(() => {
        if (currentStep.key === 'mailbox') {
            return Boolean(bootstrap?.mailbox?.connected);
        }
        if (currentStep.key === 'trial') {
            return !trialSubmitting;
        }
        if (currentStep.key === 'profile') {
            return displayName.trim().length > 0;
        }
        return true;
    }, [bootstrap?.mailbox?.connected, currentStep.key, displayName, trialSubmitting]);

    const onContinue = async () => {
        if (!canContinue) return;
        if (currentStep.key === 'trial') {
            try {
                setTrialSubmitting(true);
                await subscriptionApi.startTrial(trialOptIn);
                await refresh();
                if (trialOptIn) {
                    setShowConfetti(true);
                    pushToast('success', 'Pro trial started');
                } else {
                    pushToast('success', `Free during beta — keep using ${BRAND_NAME}. Paid Pro comes after beta.`);
                }
            } catch (e: any) {
                const detail = e?.response?.data?.detail;
                pushToast('error', detail || 'Unable to enroll trial right now.');
                return;
            } finally {
                setTrialSubmitting(false);
            }
        }
        if (currentStep.key === 'profile') {
            try {
                await updateProfile({
                    display_name: displayName.trim(),
                    timezone: timezone.trim() || undefined,
                    zip_code: zipCode.trim(),
                });
            } catch (e: any) {
                const detail = e?.response?.data?.detail;
                pushToast('error', detail || 'Failed to save profile. Please try again.');
                return;
            }
        }
        if (currentStep.key === 'finish') {
            await completeOnboarding({ onboarding_version: 1 });
            navigate('/app/today');
            return;
        }
        setStepIndex((i) => Math.min(i + 1, steps.length - 1));
    };

    const onBack = () => {
        setStepIndex((i) => Math.max(i - 1, 0));
    };

    const handleConnectGmail = async () => {
        try {
            const reconnectEmail = bootstrap?.mailbox?.reconnect_email_hint?.trim();
            const res = await startMailboxGoogleOAuth(
                reconnectEmail
                    ? { connectMode: 'reconnect', emailAddress: reconnectEmail }
                    : { connectMode: 'add' },
            );
            const url = res.url;
            if (!url) throw new Error('Missing Gmail authorization URL.');
            navigateTo(url);
        } catch (e) {
            console.error('Failed to start Gmail connect', e);
            pushToast('error', 'Failed to start Gmail connection. Please try again.');
        }
    };

    const createCustomMode = async () => {
        const name = customModeName.trim();
        if (!name) return;
        setCreatingMode(true);
        setModeLimitDetail(null);
        try {
            const created = await domainsApi.createDomain(
                buildMinimalCustomModePayload(name, {
                    description: customModeDescription.trim() || undefined,
                    icon: '✨',
                }),
            );
            // Refresh list to reflect server ordering + any defaults.
            const refreshed = await domainsApi.getDomains();
            setDomainOptions(refreshed);
            await setActiveDomain(created.key);
            setCustomModeOpen(false);
            setCustomModeName('');
            setCustomModeDescription('');
            pushToast('success', `Mode "${created.label}" created`);
        } catch (e) {
            const limitDetail = parseModeLimitFromApiError(e);
            if (limitDetail) {
                setModeLimitDetail(limitDetail);
                return;
            }
            console.error('Failed to create custom mode', e);
            const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data
                ?.detail;
            const message =
                typeof detail === 'string' && detail.trim()
                    ? detail
                    : detail &&
                        typeof detail === 'object' &&
                        'message' in detail &&
                        typeof (detail as { message?: unknown }).message === 'string'
                      ? (detail as { message: string }).message
                      : 'Failed to create mode. Please try a different name.';
            pushToast('error', message);
        } finally {
            setCreatingMode(false);
        }
    };

    useEffect(() => {
        if (bootstrap && !bootstrap.needs_onboarding) {
            navigate('/app/today', { replace: true });
        }
    }, [bootstrap, navigate]);

    useEffect(() => {
        if (!showConfetti) return;
        const timeout = setTimeout(() => setShowConfetti(false), 3000);
        return () => clearTimeout(timeout);
    }, [showConfetti]);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void refresh();
                void domainsApi.getDomains().then(setDomainOptions).catch(() => undefined);
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, [refresh]);

    if (loading || !bootstrap) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center">
                <div className="flex items-center gap-3">
                    <Logo size={32} appearance="dark" className="animate-pulse" />
                    <span className="text-sm text-slate-400">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 flex items-center justify-center px-4 py-12">
            {showConfetti && ConfettiComponent && (
                <ConfettiComponent
                    width={typeof window !== 'undefined' ? window.innerWidth : 0}
                    height={typeof window !== 'undefined' ? window.innerHeight : 0}
                    recycle={false}
                    numberOfPieces={320}
                    gravity={0.24}
                />
            )}
            {/* Background orbs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-1/4 -left-32 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
            </div>

            <div className="max-w-2xl w-full relative z-10 animate-fade-in">
                <div className="rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 shadow-2xl">
                    {/* Header with logo */}
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-3">
                            <Logo variant="wordmark" size={24} appearance="dark" />
                        </div>
                        <div className="flex items-center gap-2">
                            {steps.map((s, i) => (
                                <div
                                    key={s.key}
                                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === stepIndex
                                            ? 'bg-teal-400 w-6'
                                            : i < stepIndex
                                                ? 'bg-teal-400/50'
                                                : 'bg-white/20'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Step header */}
                    <div className="mb-6">
                        <p className="text-xs text-teal-400 font-medium mb-1">Step {stepIndex + 1} of {steps.length}</p>
                        <h1 className="text-2xl font-bold">{currentStep.title}</h1>
                        <p className="text-sm text-slate-400 mt-1">{currentStep.subtitle}</p>
                    </div>

                    {currentStep.key === 'domain' && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            {domainOptions.map((d) => {
                                const selected = activeDomainKey === d.key;
                                return (
                                    <button
                                        key={d.key}
                                        onClick={() => setActiveDomain(d.key)}
                                        className={`text-left rounded-2xl border px-5 py-5 transition-all duration-200 ${selected
                                                ? 'border-teal-400/50 bg-teal-500/10 ring-1 ring-teal-400/20'
                                                : 'border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <div className={`text-base font-semibold ${selected ? 'text-teal-300' : 'text-slate-100'}`}>
                                                {d.label}
                                            </div>
                                            {selected && (
                                                <div className="w-5 h-5 rounded-full bg-teal-400 flex items-center justify-center">
                                                    <CheckCircle className="w-3 h-3 text-slate-900" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-400 leading-relaxed">
                                            {d.description || 'Domain configuration'}
                                        </div>
                                    </button>
                                );
                            })}

                            {/* Custom mode */}
                            <button
                                onClick={() => setCustomModeOpen((v) => !v)}
                                className={`text-left rounded-2xl border px-5 py-5 transition-all duration-200 ${customModeOpen
                                    ? 'border-teal-400/50 bg-teal-500/10 ring-1 ring-teal-400/20'
                                    : 'border-dashed border-white/20 bg-white/5 hover:bg-white/10 hover:border-white/30'
                                    }`}
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-teal-400"><Plus className="w-4 h-4" /></span>
                                    <div className="text-base font-semibold text-slate-100">Create your own mode</div>
                                </div>
                                <div className="text-xs text-slate-400 leading-relaxed">
                                    Prefer a focused job-search workflow? Name a custom mode and {BRAND_NAME} will use it across the app.
                                </div>
                            </button>

                            {customModeOpen && (
                                <div className="sm:col-span-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-5">
                                    <div className="grid gap-4 sm:grid-cols-2">
                                        <Input
                                            label="Mode name"
                                            value={customModeName}
                                            onChange={setCustomModeName}
                                            placeholder="e.g., Interview Prep"
                                        />
                                        <Input
                                            label="Description (optional)"
                                            value={customModeDescription}
                                            onChange={setCustomModeDescription}
                                            placeholder="What kind of job-search work should land here?"
                                        />
                                    </div>
                                    <div className="flex items-center justify-end gap-2 mt-4">
                                        <button
                                            onClick={() => {
                                                setCustomModeOpen(false);
                                                setModeLimitDetail(null);
                                            }}
                                            className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-200 hover:bg-white/5 hover:border-white/20 transition-all duration-200"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={createCustomMode}
                                            disabled={creatingMode || customModeName.trim().length === 0}
                                            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.01] transition-all duration-200 disabled:opacity-60 disabled:hover:scale-100"
                                        >
                                            {creatingMode ? 'Creating…' : 'Create Mode'}
                                        </button>
                                    </div>
                                    {modeLimitDetail ? (
                                        <div className="mt-4">
                                            <ModeLimitBlock
                                                detail={modeLimitDetail}
                                                context="create"
                                                surface="onboarding"
                                                onChooseBuiltInMode={() => {
                                                    setCustomModeOpen(false);
                                                    setModeLimitDetail(null);
                                                    setCustomModeName('');
                                                    setCustomModeDescription('');
                                                }}
                                            />
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    )}

                    {currentStep.key === 'mailbox' && (
                        <div className="space-y-3">
                            <p className="text-sm text-slate-300">
                                Gmail is required for onboarding value. Access is read-only and least-privilege.
                            </p>
                            <div className="space-y-2 mb-2">
                                <PermissionRow
                                    title="Read-only access"
                                    desc="We can view emails but never send, delete, or modify them."
                                    icon={<Eye className="h-4 w-4" />}
                                />
                                <PermissionRow
                                    title="Headers first"
                                    desc="Sync fetches subject/sender; full content loads when you open."
                                    icon={<Filter className="h-4 w-4" />}
                                />
                                <PermissionRow
                                    title="Disconnect anytime"
                                    desc="Disconnect in Profile to stop syncing. Saved items stay; removing provider access may take longer."
                                    icon={<Power className="h-4 w-4" />}
                                />
                            </div>
                            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="text-sm font-semibold">
                                            {bootstrap.mailbox.connected ? 'Gmail connected' : 'Not connected'}
                                        </div>
                                        <div className="text-xs text-slate-400">
                                            {bootstrap.mailbox.connected
                                                ? bootstrap.mailbox.primary_email || 'Connected account'
                                                : 'Connect to ingest emails.'}
                                        </div>
                                    </div>
                                    {bootstrap.mailbox.connected ? (
                                        <span className="text-xs text-emerald-300">Connected</span>
                                    ) : null}
                                </div>
                                {!bootstrap.mailbox.connected && (
                                    <button
                                        onClick={handleConnectGmail}
                                        className="mt-4 px-5 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.01] transition-all duration-200"
                                    >
                                        Connect Gmail
                                    </button>
                                )}
                                {bootstrap.mailbox.connected && (
                                    <div className="mt-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-100 text-sm flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4" />
                                        Connected securely. You can disconnect anytime in Profile.
                                    </div>
                                )}
                                {!bootstrap.mailbox.connected && (
                                    <div className="mt-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-100 text-xs">
                                        Connect Gmail to continue onboarding.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {currentStep.key === 'trial' && (
                        <div className="space-y-4">
                            <div className="rounded-2xl border border-teal-500/30 bg-teal-500/10 p-4">
                                <p className="text-sm text-slate-100 font-semibold">Try Pro for 14 days?</p>
                                <p className="text-xs text-slate-300 mt-1">
                                    Try Pro for 14 days — no card. More mailboxes and photo credits. {BRAND_NAME} still needs you to triage.
                                </p>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <button
                                    onClick={() => setTrialOptIn(true)}
                                    className={`rounded-2xl border px-4 py-4 text-left transition-all ${trialOptIn
                                        ? 'border-teal-400/60 bg-teal-500/15 ring-1 ring-teal-400/20'
                                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="text-sm font-semibold text-slate-100">Yes, activate Pro trial</div>
                                    <div className="text-xs text-slate-400 mt-1">Recommended for full product value</div>
                                </button>
                                <button
                                    onClick={() => setTrialOptIn(false)}
                                    className={`rounded-2xl border px-4 py-4 text-left transition-all ${!trialOptIn
                                        ? 'border-slate-300/40 bg-white/10 ring-1 ring-slate-300/20'
                                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                                        }`}
                                >
                                    <div className="text-sm font-semibold text-slate-100">No, stay on Free</div>
                                    <div className="text-xs text-slate-400 mt-1">Free during beta — keep using {BRAND_NAME}.</div>
                                </button>
                            </div>
                            {trialOptIn && (
                                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">
                                    Free during beta — keep using {BRAND_NAME}. Paid Pro comes after beta.
                                </div>
                            )}
                        </div>
                    )}

                    {currentStep.key === 'profile' && (
                        <div className="grid gap-4 sm:grid-cols-2">
                            <Input
                                label="Display name"
                                value={displayName}
                                onChange={setDisplayName}
                                placeholder="e.g., Ramin"
                                autoComplete="name"
                            />
                            <Input
                                label="Timezone"
                                value={timezone}
                                onChange={setTimezone}
                                placeholder="America/Los_Angeles"
                                list="timezone-options"
                                autoComplete="off"
                            />
                            <Input
                                label="Zip code (optional)"
                                value={zipCode}
                                onChange={setZipCode}
                                placeholder="e.g., 97035"
                                inputMode="numeric"
                                autoComplete="postal-code"
                            />
                            <datalist id="timezone-options">
                                {timezoneOptions.map((tz) => (
                                    <option key={tz} value={tz} />
                                ))}
                            </datalist>
                        </div>
                    )}

                    {currentStep.key === 'finish' && (
                        <div className="rounded-2xl border border-teal-400/20 bg-teal-500/10 p-6 text-center">
                            <div className="w-12 h-12 rounded-full bg-teal-400/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-6 h-6 text-teal-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-100 mb-2">You're all set!</h3>
                            <p className="text-sm text-slate-400">
                                Click "Launch {BRAND_NAME}" to enter your workspace. You can always adjust settings later.
                            </p>
                        </div>
                    )}

                    <div className="flex items-center justify-between pt-6 mt-6 border-t border-white/10">
                        <button
                            onClick={onBack}
                            disabled={stepIndex === 0}
                            className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-300 hover:bg-white/5 hover:text-slate-100 hover:border-white/20 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-300 transition-all duration-200"
                        >
                            Back
                        </button>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onContinue}
                                disabled={!canContinue}
                                className="px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400 text-slate-900 font-semibold shadow-lg shadow-teal-500/25 hover:shadow-xl hover:shadow-teal-500/30 hover:scale-[1.01] transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100"
                            >
                                {currentStep.key === 'finish'
                                    ? `Launch ${BRAND_NAME}`
                                    : currentStep.key === 'trial'
                                        ? trialSubmitting
                                            ? 'Activating…'
                                            : trialOptIn
                                                ? 'Start Free 14-Day Pro Trial'
                                                : 'Continue on Free'
                                        : 'Continue'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {toast && (
                <div className="fixed bottom-6 right-6 z-[60] max-w-sm">
                    <div
                        className={`rounded-2xl border px-4 py-3 shadow-2xl backdrop-blur-xl ${toast.type === 'success'
                            ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100'
                            : 'border-rose-400/30 bg-rose-500/10 text-rose-100'
                            }`}
                    >
                        <div className="text-sm font-semibold">{toast.type === 'success' ? 'Saved' : 'Something went wrong'}</div>
                        <div className="text-xs opacity-90 mt-0.5">{toast.message}</div>
                    </div>
                </div>
            )}
        </div>
    );
};

const Input = ({
    label,
    value,
    onChange,
    placeholder,
    type = 'text',
    list,
    inputMode,
    autoComplete,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
    type?: string;
    list?: string;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    autoComplete?: string;
}) => (
    <label className="block">
        <div className="text-xs text-slate-400 mb-2 font-medium">{label}</div>
        <input
            type={type}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            list={list}
            inputMode={inputMode}
            autoComplete={autoComplete}
            className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-teal-400/50 focus:ring-1 focus:ring-teal-400/20 transition-all duration-200"
        />
    </label>
);

const PermissionRow = ({
    title,
    desc,
    icon,
}: {
    title: string;
    desc: string;
    icon: React.ReactNode;
}) => (
    <div className="flex items-start gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3 hover:bg-white/10 transition-colors duration-200">
        <div className="mt-0.5 text-teal-400">{icon}</div>
        <div>
            <div className="text-sm font-medium text-slate-100">{title}</div>
            <div className="text-xs text-slate-400 leading-relaxed">{desc}</div>
        </div>
    </div>
);

export default OnboardingPage;
